'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { useAuthNavigation } from '@eliadmin/core/src/components/dlz/auth/dialog/auth-navigation';
import { ActionButton } from '@eliadmin/core/src/components/dlz/auth/password-reset/action-link';
import { FormAlert } from '@eliadmin/core/src/components/dlz/auth/password-reset/form-alert';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import { StepHeading } from '@eliadmin/core/src/components/dlz/auth/password-reset/step-heading';
import { SupportHelp } from '@eliadmin/core/src/components/dlz/auth/password-reset/support-help';
import { useRequestError } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-request-error';
import { useSendResetLink } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-send-reset-link';
import { forgotPasswordSchema } from '@eliadmin/core/src/components/auth/forgot-password-schema';
import { FormRow } from '@eliadmin/core/src/components/dlz/forms/form-row';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form, FormField } from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type EmailValues = z.infer<ReturnType<typeof forgotPasswordSchema>>;

const RENDERED_FIELDS = ['email'] as const;

/** Step 1 – „Zabudli ste heslo?“ (Figma 60155:20067, mobile 60438:81985). */
export function EmailStep({
  defaultEmail = '',
  supportPhone,
  supportHours,
  onSent,
}: {
  defaultEmail?: string;
  supportPhone?: string;
  supportHours?: string;
  onSent: (email: string) => void;
}) {
  const { t } = useTranslations();
  const { showLogin } = useAuthNavigation();
  const { formValidationMessages } = useFormValidationMessages(t);
  const sendLink = useSendResetLink();
  const form = useForm<EmailValues>({
    resolver: zodResolver(forgotPasswordSchema(formValidationMessages)),
    defaultValues: { email: defaultEmail },
  });
  const { alert, reportError, clearAlert } = useRequestError(form, RENDERED_FIELDS);

  const submit = form.handleSubmit(async ({ email }) => {
    clearAlert();
    try {
      await sendLink(email);
      onSent(email);
    } catch (error) {
      reportError(error);
    }
  });

  return (
    <>
      <Form {...form}>
        <form noValidate onSubmit={submit} className="flex w-full flex-col items-center gap-6">
          <StepHeading
            icon="at"
            iconSize={32}
            title={RESET_COPY.email.title}
            description={RESET_COPY.email.text}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormRow label={RESET_COPY.email.label} required className="w-full">
                <Input
                  {...field}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={RESET_COPY.email.placeholder}
                  data-autofocus=""
                />
              </FormRow>
            )}
          />
          <FormAlert>{alert}</FormAlert>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            className="h-12 w-full md:h-[52px] md:w-auto"
          >
            {RESET_COPY.email.submit}
          </Button>
        </form>
      </Form>
      <ActionButton onClick={showLogin}>{RESET_COPY.email.back}</ActionButton>
      <SupportHelp phone={supportPhone} hours={supportHours} />
    </>
  );
}
